import React, { useRef } from 'react';

/**
 * Card — elevated near-black surface with a cursor-tracking spotlight
 * and a subtle 3D tilt toward the pointer. The default container for
 * projects and experience entries. Set `interactive={false}` for static.
 */
export function Card({
  children,
  interactive = true,
  tilt = 6,
  padding = 'var(--space-6)',
  style = {},
  ...rest
}) {
  const ref = useRef(null);
  const glowRef = useRef(null);

  const onMove = (e) => {
    if (!interactive) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    el.style.transform = `perspective(900px) rotateX(${(0.5 - py) * tilt}deg) rotateY(${(px - 0.5) * tilt}deg) translateY(-4px)`;
    if (glowRef.current) {
      glowRef.current.style.opacity = '1';
      glowRef.current.style.background = `radial-gradient(380px circle at ${px * 100}% ${py * 100}%, var(--yellow-a16), transparent 55%)`;
    }
  };
  const onLeave = () => {
    if (!interactive) return;
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'perspective(900px) rotateX(0) rotateY(0) translateY(0)';
    el.style.borderColor = 'var(--border-subtle)';
    if (glowRef.current) glowRef.current.style.opacity = '0';
  };
  const onEnter = () => {
    if (!interactive || !ref.current) return;
    ref.current.style.borderColor = 'var(--yellow-a32)';
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onMouseEnter={onEnter}
      style={{
        position: 'relative',
        padding,
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-elevated)',
        border: 'var(--border-hairline) solid var(--border-subtle)',
        boxShadow: 'var(--shadow-md)',
        transformStyle: 'preserve-3d',
        transition: 'transform var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out)',
        overflow: 'hidden',
        ...style,
      }}
      {...rest}
    >
      <div ref={glowRef} aria-hidden="true" style={{ position: 'absolute', inset: 0, opacity: 0, transition: 'opacity var(--dur-base) var(--ease-out)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative' }}>{children}</div>
    </div>
  );
}

export default Card;
